import React from "react"
import { Link } from "react-router-dom"

import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"

import api from "../../services/api"

import "./AdminAudit.scss"

class AdminAudit extends React.Component {
  constructor() {
    super()

    this.state = {
      loading: true,
      error: "",
      summary: null,
      conflicts: [],
      slots: [],
      monthlySummaries: [],
      activeFilter: "all",
      expandedMonths: [],
      monthSlotsCache: {},
      loadingMonths: [],
    }
  }

  async componentDidMount() {
    const userId = localStorage.getItem("userId")

    if (!userId) {
      this.props.history.push("/login")
      return
    }

    try {
      const response = await api.get(
        "/admin/reports/barista-capacity?details=true",
      )

      this.setState({
        summary: response.data.summary,
        conflicts: response.data.conflicts || [],
        slots: response.data.slots || [],
        monthlySummaries: response.data.monthlySummaries || [],
        loading: false,
      })
    } catch (error) {
      this.setState({
        error: "Could not load audit information. Please try again.",
        loading: false,
      })
    }
  }

  formatDate(dateKey) {
    if (!dateKey) return ""

    const [year, month, day] = dateKey.split("-")

    if (!year || !month || !day) return dateKey

    return `${day}/${month}/${year}`
  }

  formatMonth(monthKey) {
    if (!monthKey) return ""

    const [year, month] = monthKey.split("-")

    if (!year || !month) return monthKey

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ]

    const monthName = monthNames[Number(month) - 1] || month

    return `${monthName} ${year}`
  }

  getStatusLabel(status) {
    if (status === "overbooked") return "Overbooked"
    if (status === "missing_calendar_slot") return "Missing calendar slot"
    if (status === "past_archived_slot") return "Past archived slot"
    if (status === "warning") return "Warning"
    if (status === "full") return "Full"

    return "OK"
  }

  handleFilter(filter) {
    this.setState({ activeFilter: filter })
  }

  formatMonthSummaryLabel(month) {
    const slotsLabel = `${month.slotsCount} slot${
      month.slotsCount === 1 ? "" : "s"
    }`

    if (month.knownLimitTotal > 0) {
      return `${month.totalBooked} of ${month.knownLimitTotal} booked across ${slotsLabel}`
    }

    return `${month.totalBooked} booked across ${slotsLabel} (archived — no capacity limit on record)`
  }

  async handleMonthClick(monthKey) {
    const { expandedMonths, monthSlotsCache, loadingMonths } = this.state

    if (expandedMonths.includes(monthKey)) {
      this.setState({
        expandedMonths: expandedMonths.filter((key) => key !== monthKey),
      })
      return
    }

    if (monthSlotsCache[monthKey]) {
      this.setState({ expandedMonths: [...expandedMonths, monthKey] })
      return
    }

    this.setState({
      expandedMonths: [...expandedMonths, monthKey],
      loadingMonths: [...loadingMonths, monthKey],
    })

    try {
      const response = await api.get(
        `/admin/reports/barista-capacity?month=${monthKey}&details=true`,
      )

      this.setState((prevState) => ({
        monthSlotsCache: {
          ...prevState.monthSlotsCache,
          [monthKey]: response.data.slots || [],
        },
        loadingMonths: prevState.loadingMonths.filter(
          (key) => key !== monthKey,
        ),
      }))
    } catch (error) {
      this.setState((prevState) => ({
        expandedMonths: prevState.expandedMonths.filter(
          (key) => key !== monthKey,
        ),
        loadingMonths: prevState.loadingMonths.filter(
          (key) => key !== monthKey,
        ),
        error: "Could not load bookings for that month. Please try again.",
      }))
    }
  }

  getFilteredSlots() {
    const { slots, activeFilter } = this.state

    if (!Array.isArray(slots)) return []

    if (activeFilter === "all") {
      return slots
    }

    if (activeFilter === "booked") {
      return slots.filter((slot) => slot.booked > 0)
    }

    return slots.filter((slot) => slot.status === activeFilter)
  }

  groupSlotsByDate(slots) {
    if (!Array.isArray(slots)) return []

    const grouped = {}

    slots.forEach((slot) => {
      if (!grouped[slot.date]) {
        grouped[slot.date] = []
      }

      grouped[slot.date].push(slot)
    })

    return Object.keys(grouped)
      .sort()
      .map((date) => ({
        date,
        slots: grouped[date].sort((a, b) => a.hour.localeCompare(b.hour)),
      }))
  }

  renderScheduleGroups(groupedSlots) {
    return groupedSlots.map((day) => (
      <div className="day-card" key={day.date}>
        <h3>{this.formatDate(day.date)}</h3>

        {day.slots.map((slot) => (
          <div
            className={`slot-card ${slot.status}`}
            key={`${slot.date}-${slot.hour}`}
          >
            <div className="slot-header">
              <div>
                <strong>{slot.hour}</strong>
                <span>{this.getStatusLabel(slot.status)}</span>
              </div>

              <div className="slot-capacity">
                <span>
                  Booked: <strong>{slot.booked}</strong>
                </span>
                <span>
                  Limit: <strong>{slot.limit}</strong>
                </span>
                <span>
                  Available: <strong>{slot.available}</strong>
                </span>
              </div>
            </div>

            {slot.bookings && slot.bookings.length > 0 ? (
              <div className="slot-bookings">
                {slot.bookings.map((booking) => (
                  <div className="customer-card" key={booking.userCourseId}>
                    <strong>{booking?.user?.name || "-"}</strong>
                    <span>{booking?.user?.email || "-"}</span>
                    <span>{booking?.user?.phone || "-"}</span>
                    <span>Qty: {booking.quantity}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-slot">No bookings yet.</p>
            )}
          </div>
        ))}
      </div>
    ))
  }

  render() {
    const {
      loading,
      error,
      summary,
      monthlySummaries,
      activeFilter,
      expandedMonths,
      monthSlotsCache,
      loadingMonths,
    } = this.state
    const filteredSlots = this.getFilteredSlots()
    const groupedSlots = this.groupSlotsByDate(filteredSlots)

    return (
      <div className="container admin-audit">
        <div className="audit-header">
          <div>
            <p className="eyebrow">Admin area</p>
            <h1>Audit Bookings</h1>
            <p>
              Check Barista course capacity, overbooked slots, and calendar
              conflicts.
            </p>
          </div>

          <Link className="back-link" to="/dashboard">
            <ArrowBackIcon /> Back to Admin
          </Link>
        </div>

        {loading ? <p className="audit-loading">Loading audit...</p> : ""}

        {error ? <div className="audit-error">{error}</div> : ""}

        {!loading && !error && summary ? (
          <div className="audit-summary">
            <button
              type="button"
              className={activeFilter === "all" ? "active" : ""}
              onClick={() => this.handleFilter("all")}
            >
              <strong>{summary.totalSlotsChecked}</strong>
              <span>Total slots</span>
            </button>

            <button
              type="button"
              className={activeFilter === "booked" ? "active" : ""}
              onClick={() => this.handleFilter("booked")}
            >
              <strong>
                {this.state.slots.filter((slot) => slot.booked > 0).length}
              </strong>
              <span>Booked slots</span>
            </button>

            <button
              type="button"
              className={activeFilter === "overbooked" ? "active danger" : ""}
              onClick={() => this.handleFilter("overbooked")}
            >
              <strong>{summary.overbooked}</strong>
              <span>Overbooked</span>
            </button>

            <button
              type="button"
              className={
                activeFilter === "missing_calendar_slot" ? "active danger" : ""
              }
              onClick={() => this.handleFilter("missing_calendar_slot")}
            >
              <strong>{summary.missingCalendarSlot}</strong>
              <span>Missing slots</span>
            </button>

            <button
              type="button"
              className={activeFilter === "warning" ? "active warning" : ""}
              onClick={() => this.handleFilter("warning")}
            >
              <strong>{summary.warning}</strong>
              <span>Warnings</span>
            </button>

            <button
              type="button"
              className={activeFilter === "full" ? "active" : ""}
              onClick={() => this.handleFilter("full")}
            >
              <strong>{summary.full}</strong>
              <span>Full</span>
            </button>
          </div>
        ) : (
          ""
        )}

        {!loading && !error ? (
          <div className="active-filter-label">
            Showing: <strong>{this.getStatusLabel(activeFilter)}</strong>
          </div>
        ) : (
          ""
        )}

        {!loading && !error && groupedSlots.length > 0 ? (
          <div className="barista-schedule">
            <h2>Barista Schedule</h2>
            <p className="section-description">
              View bookings by date and time, including customer contact
              details.
            </p>

            {this.renderScheduleGroups(groupedSlots)}
          </div>
        ) : (
          ""
        )}

        {!loading && !error && monthlySummaries.length > 0 ? (
          <div className="barista-history">
            <h2>Previous Months</h2>
            <p className="section-description">
              Aggregated totals only — click a month to load its detailed
              bookings.
            </p>

            {monthlySummaries.map((month) => {
              const isExpanded = expandedMonths.includes(month.monthKey)
              const isLoading = loadingMonths.includes(month.monthKey)
              const monthSlots = monthSlotsCache[month.monthKey] || []
              const groupedMonthSlots = this.groupSlotsByDate(monthSlots)

              return (
                <div
                  className={`month-summary-card ${
                    isExpanded ? "expanded" : ""
                  }`}
                  key={month.monthKey}
                >
                  <button
                    type="button"
                    className="month-summary-toggle"
                    onClick={() => this.handleMonthClick(month.monthKey)}
                  >
                    <strong>{this.formatMonth(month.monthKey)}</strong>
                    <span>{this.formatMonthSummaryLabel(month)}</span>
                  </button>

                  {isExpanded ? (
                    <div className="month-summary-detail">
                      {isLoading ? (
                        <p className="audit-loading">
                          Loading month details...
                        </p>
                      ) : (
                        this.renderScheduleGroups(groupedMonthSlots)
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          ""
        )}

        {!loading && !error && groupedSlots.length === 0 ? (
          <div className="audit-success">
            <CheckCircleIcon />
            <div>
              <h3>No slots found for this filter</h3>
              <p>Try selecting another status above.</p>
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    )
  }
}

export default AdminAudit
